import { Suspense, lazy } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import ContactForm from "@/components/ContactForm";
import Reveal from "@/components/Reveal";
import { useProgressRef } from "@/three/useProgressRef";

import wideHero from "@/assets/widehero.jpg";
import closeUpHero from "@/assets/closeuphero.jpg";
import caseExplo from "@/assets/caseexplo.jpg";
import caseImg from "@/assets/case.jpg";
import cameraImg from "@/assets/camera.jpg";
import startGate from "@/assets/startgate.jpg";
import finishGate from "@/assets/finishgate.jpg";

/* Only the two scenes the design keeps are 3D; the rest are image-driven. */
const ProblemScene = lazy(() => import("@/three/ProblemScene"));
const VisionScene = lazy(() => import("@/three/VisionScene"));

/* Tall wrapper that pins a full-screen canvas + content overlay */
function ScrollSection({ id, height = "h-[300vh]", children, testId, className = "" }) {
  return (
    <section id={id} data-testid={testId} className={`relative ${height} ${className}`}>
      {children}
    </section>
  );
}

/* Live status pill */
function StatusStrip() {
  return (
    <div
      className="inline-flex items-center gap-3 border border-white/15 bg-black/40 px-4 py-2 backdrop-blur-xl"
      data-testid="hero-status-strip"
    >
      <span className="relative inline-flex h-2 w-2">
        <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#ff5500]" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff5500]" />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
        ARVEX · CAPTURE LIVE
      </span>
    </div>
  );
}

const EASE = [0.22, 1, 0.36, 1];

export default function Landing() {
  const problem = useProgressRef();
  const vision = useProgressRef();

  // Subtle parallax on the hero background image
  const { scrollYProgress } = useScroll();
  const heroBgY = useTransform(scrollYProgress, [0, 0.18], [0, 120]);
  const heroBgScale = useTransform(scrollYProgress, [0, 0.18], [1.05, 1.18]);

  return (
    <div className="relative bg-[#050505] text-white" id="top">
      <Nav />

      {/* ==================== HERO ==================== */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative flex min-h-screen items-center overflow-hidden"
      >
        {/* Background image + parallax */}
        <motion.div
          style={{ y: heroBgY, scale: heroBgScale }}
          className="absolute inset-0 z-0"
        >
          <motion.img
            src={wideHero}
            alt="Ski racer captured mid-run on an alpine course"
            className="h-full w-full object-cover"
            initial={{ opacity: 0, scale: 1.12 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: EASE }}
          />
        </motion.div>

        {/* Cinematic overlays for legibility */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

        {/* Content */}
        <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-12 px-6 pt-28 pb-20 md:px-10 lg:grid-cols-12 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
            >
              <StatusStrip />
            </motion.div>

            <motion.div
              className="label-mono mt-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
            >
              ARVEX · Ski Race Capture System
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-5 text-balance text-4xl font-semibold leading-[1.04] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
            >
              {["Capture every run.", "Analyze every turn.", "Improve every session."].map(
                (line, i) => (
                  <motion.span
                    key={line}
                    className={
                      i === 0
                        ? "block"
                        : i === 1
                        ? "block text-white/55"
                        : "block bg-gradient-to-r from-[#ff5500] via-[#ff8a3d] to-[#00e5ff] bg-clip-text text-transparent"
                    }
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.18 + i * 0.12, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                )
              )}
            </h1>

            <motion.p
              className="mt-7 max-w-xl text-base text-white/65 md:text-lg"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            >
              The autonomous capture &amp; timing system for ski race training. Films every
              run, measures every gate, delivers every clip — directly to the athlete&apos;s
              phone.
            </motion.p>

            <motion.div
              className="mt-9 flex flex-wrap items-center gap-3"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
            >
              <a
                href="#waitlist"
                data-testid="hero-cta-primary"
                className="btn-primary rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.22em]"
              >
                Request Early Access
              </a>
              <a
                href="#device"
                data-testid="hero-cta-secondary"
                className="btn-secondary rounded-full px-6 py-3 font-mono text-xs uppercase tracking-[0.22em]"
              >
                See the System →
              </a>
            </motion.div>
          </div>

          {/* Floating close-up card */}
          <div className="lg:col-span-5">
            <motion.div
              className="relative ml-auto w-full max-w-md"
              initial={{ opacity: 0, y: 40, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
            >
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="glass relative overflow-hidden rounded-2xl p-2"
              >
                <div className="overflow-hidden rounded-xl">
                  <img
                    src={closeUpHero}
                    alt="Close-up of the Arvex capture unit on the slope"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
                {/* HUD overlay */}
                <div className="pointer-events-none absolute inset-2 rounded-xl">
                  <div className="absolute left-3 top-3 flex items-center gap-2">
                    <span className="relative inline-flex h-2 w-2">
                      <span className="pulse-dot absolute inline-flex h-full w-full rounded-full bg-[#ff2a00]" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff2a00]" />
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/80">
                      REC
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-[0.3em] text-white/70">
                    4K · 120FPS
                  </div>
                  <span className="absolute left-3 bottom-3 h-3 w-3 border-b border-l border-white/40" />
                  <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-white/40" />
                </div>
              </motion.div>
              <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">
                <span>Autonomous unit</span>
                <span>Live feed</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          className="pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
            Scroll
          </div>
          <div className="mx-auto mt-2 h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
        </motion.div>
      </section>

      {/* ==================== STATS MARQUEE ==================== */}
      <section className="relative z-10 overflow-hidden border-y border-white/10 bg-black py-6">
        <div className="marquee-track flex w-max items-center gap-16 whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <div className="flex items-center gap-16" key={k}>
              {[
                "AUTONOMOUS CAPTURE", "/ /", "1/1000s TIMING", "/ /", "OFFLINE READY", "/ /",
                "INSTANT TO PHONE", "/ /", "BUILT FOR -25°C", "/ /", "CLUB GRADE", "/ /",
              ].map((s, i) => (
                <span
                  key={i}
                  className={`font-mono text-xs uppercase tracking-[0.3em] ${
                    s.startsWith("/") ? "text-[#ff5500]" : "text-white/80"
                  }`}
                >
                  {s}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ==================== 01 · PROBLEM (3D — kept) ==================== */}
      <ScrollSection id="problem" height="h-[150vh]" testId="problem-section">
        <div ref={problem.sectionRef} className="absolute inset-0">
          <div className="sticky top-0 h-screen w-full overflow-hidden">
            <Suspense fallback={<SceneFallback />}>
              <ProblemScene progressRef={problem.progressRef} />
            </Suspense>
            <div className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-[1400px] flex-col justify-center px-6 md:px-16">
              <div className="label-mono mb-6">01 · The Problem</div>
              <h2 className="font-display max-w-2xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                The best run of the day. <br />
                <span className="text-white/50">And nobody filmed it.</span>
              </h2>
              <div className="mt-10 grid max-w-3xl gap-6 md:grid-cols-3">
                <Bullet
                  label="Missing footage"
                  text="Coaches can&apos;t be in two places at once. Most runs go uncaptured."
                />
                <Bullet
                  label="Lost feedback"
                  text="Without video, the technical detail of every turn is gone forever."
                />
                <Bullet
                  label="Manual chaos"
                  text="Phones, stopwatches, USB sticks. Training shouldn&apos;t be admin."
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollSection>

      {/* ==================== 02 · DEVICE (image-driven) ==================== */}
      <section
        id="device"
        data-testid="device-section"
        className="relative z-10 overflow-hidden bg-[#050505] py-28 md:py-40"
      >
        {/* ambient glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            {/* Copy */}
            <div className="order-2 lg:order-1">
              <Reveal>
                <div className="label-mono mb-6">02 · The Arvex Device</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display max-w-md text-balance text-4xl font-semibold leading-tight tracking-tight md:text-5xl">
                  One unit. <br /> Four jobs.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-5 max-w-md text-base text-white/60">
                  A weatherproof capture unit that sees the slope, times every gate, and
                  uploads every clip — without anyone touching it.
                </p>
              </Reveal>

              <div className="mt-10 space-y-5">
                {[
                  { no: "01", name: "Camera", desc: "4K wide-angle, auto-tracking the racer", color: "#00e5ff" },
                  { no: "02", name: "Capture", desc: "Edge-recording with on-device buffering", color: "#ffffff" },
                  { no: "03", name: "Timing", desc: "Sub-millisecond gate trigger sync", color: "#ff5500" },
                  { no: "04", name: "Upload", desc: "LTE/Wi-Fi push directly to the athlete app", color: "#00e5ff" },
                ].map((d, i) => (
                  <Reveal key={d.no} delay={0.12 + i * 0.08} direction="left">
                    <DeviceLabel {...d} />
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Exploded view */}
            <div className="order-1 lg:order-2">
              <Reveal direction="right" duration={0.9}>
                <FloatCard>
                  <ImageFrame
                    src={caseExplo}
                    alt="Exploded view of the Arvex capture unit internals"
                    badge="Exploded view"
                    ratio="aspect-[16/10]"
                  />
                </FloatCard>
              </Reveal>
            </div>
          </div>

          {/* Supporting product shots */}
          <div className="mt-16 grid gap-6 md:mt-24 md:grid-cols-2">
            <Reveal delay={0.05}>
              <ImageFrame
                src={caseImg}
                alt="The sealed Arvex weatherproof housing"
                caption="Weatherproof housing"
                sub="Sealed against snow, ice and -25°C"
                ratio="aspect-[16/9]"
              />
            </Reveal>
            <Reveal delay={0.12}>
              <ImageFrame
                src={cameraImg}
                alt="The Arvex auto-tracking optics module"
                caption="Auto-tracking optics"
                sub="4K wide-angle that follows the racer"
                ratio="aspect-[16/9]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ==================== 03 · THE RUN (image-driven) ==================== */}
      <section
        id="run"
        data-testid="run-section"
        className="relative z-10 overflow-hidden border-t border-white/10 bg-black py-28 md:py-40"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute right-0 top-10 h-[420px] w-[420px] rounded-full bg-[#ff5500]/[0.07] blur-3xl" />
          <div className="absolute left-0 bottom-10 h-[420px] w-[420px] rounded-full bg-[#00e5ff]/[0.06] blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <div className="label-mono mb-4">03 · The Run</div>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display max-w-3xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              Gate triggers.
              <br />
              <span className="text-[#ff5500]">Camera fires.</span>{" "}
              <span className="text-white/40">·</span>{" "}
              <span className="text-[#00e5ff]">Clip lands on the phone.</span>
            </h2>
          </Reveal>

          {/* Start / Finish gate imagery */}
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal direction="left" delay={0.05}>
              <FloatCard amp={10} dur={8}>
                <ImageFrame
                  src={startGate}
                  alt="Athlete breaking the Arvex start gate"
                  badge="Start gate"
                  caption="The clock starts itself"
                  sub="Beam breaks → capture begins"
                  accent="#ff5500"
                  ratio="aspect-[16/10]"
                />
              </FloatCard>
            </Reveal>
            <Reveal direction="right" delay={0.12}>
              <FloatCard amp={10} dur={9}>
                <ImageFrame
                  src={finishGate}
                  alt="Athlete crossing the Arvex finish gate"
                  badge="Finish gate"
                  caption="The clip uploads itself"
                  sub="Beam breaks → auto-upload to app"
                  accent="#00e5ff"
                  ratio="aspect-[16/10]"
                />
              </FloatCard>
            </Reveal>
          </div>

          {/* Sequence steps */}
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              { n: "01", t: "Start gate breaks" },
              { n: "02", t: "Capture begins" },
              { n: "03", t: "Finish stops the clock" },
              { n: "04", t: "Auto-upload to app" },
            ].map((s, i) => (
              <Reveal key={s.n} delay={0.05 + i * 0.08}>
                <Step n={s.n} t={s.t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 04 · VISION (3D — kept) ==================== */}
      <ScrollSection id="vision" height="h-[170vh]" testId="vision-section">
        <div ref={vision.sectionRef} className="absolute inset-0">
          <div className="sticky top-0 h-screen w-full overflow-hidden">
            <Suspense fallback={<SceneFallback />}>
              <VisionScene progressRef={vision.progressRef} />
            </Suspense>
            <div className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-[1400px] flex-col justify-between px-6 py-24 md:px-16">
              <div>
                <div className="label-mono mb-4">04 · Future Vision</div>
                <h2 className="font-display max-w-3xl text-balance text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
                  Beyond capture.
                  <br />
                  <span className="text-white/50">Toward understanding.</span>
                </h2>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <VisionCard
                  badge="Line Analysis"
                  text="Overlay the ideal racing line against every athlete's actual path."
                  color="#ff5500"
                />
                <VisionCard
                  badge="Velocity Curve"
                  text="Per-gate speed and exit-velocity, visualized turn by turn."
                  color="#ffffff"
                />
                <VisionCard
                  badge="AI Commentary"
                  text="Automated technical feedback after every single run."
                  color="#00e5ff"
                />
              </div>
            </div>
          </div>
        </div>
      </ScrollSection>

      {/* ==================== CTA / WAITLIST / CONTACT ==================== */}
      <section
        id="waitlist"
        data-testid="cta-section"
        className="relative z-10 bg-black px-6 py-28 md:px-10 md:py-40"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-1/2 h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff5500]/10 blur-3xl" />
          <div className="absolute right-10 top-20 h-[300px] w-[300px] rounded-full bg-[#00e5ff]/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-[1400px]">
          <div className="mx-auto max-w-3xl text-center">
            <Reveal>
              <div className="label-mono">Launching Winter Season</div>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-display mt-4 text-balance text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl">
                Ready for <span className="text-[#ff5500]">first tracks</span>?
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-6 max-w-xl text-white/60">
                Limited pilot units shipping to selected clubs and athletes. Join the
                waitlist or talk to us about equipping your team.
              </p>
            </Reveal>
          </div>

          <div id="contact" className="mt-16 grid gap-6 md:grid-cols-2">
            <WaitlistForm />
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ===================== Helpers ===================== */

function SceneFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#050505]">
      <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
        Loading scene…
      </div>
    </div>
  );
}

/* Continuous gentle float */
function FloatCard({ children, amp = 14, dur = 7 }) {
  return (
    <motion.div
      animate={{ y: [0, -amp, 0] }}
      transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
    >
      {children}
    </motion.div>
  );
}

/* Framed image with corner ticks, optional badge + caption */
function ImageFrame({ src, alt, badge, caption, sub, accent = "#ffffff", ratio = "aspect-[16/10]" }) {
  return (
    <figure className="glass group relative overflow-hidden rounded-2xl p-2">
      <div className={`relative overflow-hidden rounded-xl ${ratio}`}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          style={{ transitionDuration: "1200ms" }}
          className="h-full w-full object-cover transition-transform ease-out group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {badge && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-2 border border-white/15 bg-black/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-white/85 backdrop-blur-md">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
            />
            {badge}
          </span>
        )}

        {/* corner ticks */}
        <span className="absolute left-3 bottom-3 h-3 w-3 border-b border-l border-white/30" />
        <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-white/30" />

        {caption && (
          <figcaption className="absolute inset-x-3 bottom-3">
            <div className="font-display text-lg font-medium tracking-tight text-white">
              {caption}
            </div>
            {sub && (
              <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                {sub}
              </div>
            )}
          </figcaption>
        )}
      </div>
    </figure>
  );
}

function Bullet({ label, text }) {
  return (
    <div className="border-l border-white/10 pl-4">
      <div className="label-mono text-[#ff5500]">{label}</div>
      <p className="mt-2 text-sm text-white/70">{text}</p>
    </div>
  );
}

function DeviceLabel({ no, name, desc, color }) {
  return (
    <div className="flex items-start gap-4 border-l-2 border-white/10 pl-4">
      <span
        className="mt-1 inline-block h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 12px ${color}` }}
      />
      <div>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            {no}
          </span>
          <span className="font-display text-xl font-medium tracking-tight">{name}</span>
        </div>
        <p className="mt-1 text-sm text-white/55">{desc}</p>
      </div>
    </div>
  );
}

function Step({ n, t }) {
  return (
    <div className="glass h-full px-4 py-4">
      <div className="label-mono">{n}</div>
      <div className="mt-2 font-display text-base font-medium tracking-tight">{t}</div>
    </div>
  );
}

function VisionCard({ badge, text, color }) {
  return (
    <div className="glass relative overflow-hidden p-6">
      <span
        className="absolute right-4 top-4 inline-block h-2 w-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 14px ${color}` }}
      />
      <div className="label-mono" style={{ color }}>
        {badge}
      </div>
      <p className="mt-4 text-sm text-white/75">{text}</p>
    </div>
  );
}
